<script setup>
import {onBeforeUnmount, ref} from 'vue';
// pojmenováno DropzoneLib (ne Dropzone) - <script setup> by binding "Dropzone" přednostně
// napároval na šablonový tag <dropzone>, čímž by nahradil globálně registrovanou
// dropzone-vue3 komponentu holou třídou z knihovny "dropzone" a rozbil vykreslení.
import DropzoneLib from 'dropzone';
import {createConfirmDialog} from 'vuejs-confirm-dialog';

import {useI18n} from 'vue-i18n';
import {getToken} from '@/services/AuthService';
import {showErrorMessage} from '@/helper';
import ModalDialog from '@/components/modals/ModalDialog.vue';

const {t} = useI18n()

// DropzoneLib.confirm je statická metoda na celé třídě Dropzone (ne instanci), takže tento
// override se týká všech CustomDropzone v appce najednou - nahrazuje nativní window.confirm()
// vlastním modalem, aby potvrzení smazání souboru vypadalo stejně jako jinde v appce
// (např. archivace klienta).
DropzoneLib.confirm = function (question, accepted, rejected) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('customDropzone.confirmation_title'),
    message: question,
    actionType: 'DELETE',
  });
  onConfirm(() => accepted());
  onCancel(() => {
    if (rejected) {
      rejected();
    }
  });
  reveal();
};

// dropzone-vue3 volá při unmountu podkladové <dropzone> komponenty dropzone.destroy(),
// který interně spustí removeAllFiles(true) a vyemituje 'removedfile' pro KAŽDÝ soubor
// (i ty vložené jen jako náhled přes manuallyAddFile). Bez téhle pojistky by se tak při
// zavření/odmountování dropzone (např. zavření onboardingu po "Uložit jako koncept")
// zavolal onRemoveFile pro všechny zobrazené přílohy a reálně smazal i soubory, které
// uživatel nikdy neodstranil.
let isUnmounting = false;
onBeforeUnmount(() => {
  isUnmounting = true;
});

const props = defineProps({
  dropzoneOptionsInput: Object,
  uploadSuccess: Function,
  uploadError: Function,
  uploadMultipleSuccess: Function,
  uploadMultipleError: Function,
  onSend: Function,
  // volitelný callback pro smazání souboru přes vlastní tlačítko dropzone (addRemoveLinks) -
  // volá se i pro soubory vložené přes manuallyAddFile(), ne jen reálně nahrané v této session
  onRemoveFile: Function,
  previewsDisabled: {
    type: Boolean,
    default: false
  },
  acceptedFiles: {
    type: String,
    default: null
  },
  minimal: {
    type: Boolean,
    default: false
  },
});

const customDropzoneRef = ref(null);

// Limit v MB - musí odpovídat BE config('media-library.max_file_size'), jinak BE soubor
// odmítne (422) až po odeslání celého formuláře. Dropzone počítá MB jako 1024 * 1024 B,
// stejně jako BE.
const MAX_FILE_SIZE_MB = 20;

const dropzoneOptions = {
  params: {},
  thumbnailWidth: 150,
  maxFilesize: MAX_FILE_SIZE_MB,
  headers: {
    // getToken() zohledňuje i impersonaci - upload z impersonovaného panelu se tak
    // přiřadí ke správnému autorovi, ne k identitě z jiného panelu.
    "Authorization": "Bearer " + getToken(),
  },
  acceptedFiles: props.acceptedFiles,
  addRemoveLinks: true,
  timeout: 600000,
  maxFiles: 1,
  parallelUploads: 20,
  previewsContainer: "#dz-previews",
  uploadMultiple: false,
  dictFileTooBig: t('customDropzone.file_is_too_big', {max: MAX_FILE_SIZE_MB}),
  dictInvalidFileType: t('customDropzone.unauthorized_file_type'),
  dictCancelUpload: t('customDropzone.cancel_uploading'),
  dictUploadCanceled: t('customDropzone.uploading_canceled'),
  dictCancelUploadConfirmation: t('customDropzone.confirm_cancel_uploading'),
  dictRemoveFile: t('customDropzone.remove_file'),
  dictRemoveFileConfirmation: t('customDropzone.confirm_remove_file'),
  dictMaxFilesExceeded: t('customDropzone.maximum_number_files_exceeded'),
  previewTemplate: `<div id="dz-preview-template" class="dz-preview dz-file-preview ${props.previewsDisabled ? 'd-none' : ''}">
      <div class="dz-image">
      <img data-dz-thumbnail />
      </div>
      <div class="dz-details">
      <div class="dz-filename"><span data-dz-name></span></div>
      <div class="dz-size" data-dz-size></div>
      </div>
      <div class="dz-progress">
      <div class="spinner-border" role="status">
      <span class="sr-only">Loading...</span>
      </div>
       {{ t('customDropzone.uploading') }}
      </div>
      <div class="dz-success-mark"><span>✔</span></div>
      </div>`,
  ...props.dropzoneOptionsInput,
}

function onSend() {
  if (props.onSend) {
    props.onSend();
  }
}

/**
 * Dropzone emituje 'error' i pro soubor odmítnutý už při přidání (příliš velký, nepovolený
 * typ, překročený počet) - takový soubor má file.accepted === false. Při vypnutých náhledech
 * (previewsDisabled) by se hláška Dropzone nikde nezobrazila a uživatel by netušil, že soubor
 * nebyl přijat, proto ji ukážeme jako toast.
 *
 * @param {File|object} file soubor, u kterého chyba nastala
 * @param {string|object} message text chyby od Dropzone, u chyby uploadu odpověď serveru
 */
function onUploadError(file, message) {
  if (props.previewsDisabled && file?.accepted === false) {
    // u odmítnutí při přidání je message vždy text z dict* voleb (ne odpověď serveru)
    showErrorMessage(`${file.name}: ${message}`);
  }
  if (props.uploadError) {
    props.uploadError();
  }
}

function onUploadMultipleError() {
  if (props.uploadMultipleError) {
    props.uploadMultipleError();
  }
}

function uploadMultipleSuccess(files) {
  if (props.uploadMultipleSuccess) {
    props.uploadMultipleSuccess(files);
  }
  // Odstranit jen právě nahrané soubory, ne celou dropzone - removeAllFiles() by smazal
  // i soubory vložené přes manuallyAddFile() (např. dřív nahrané přílohy zobrazené v téže
  // dropzone), což by přes onRemoveFile omylem spustilo jejich smazání na backendu.
  files.forEach(file => customDropzoneRef.value.removeFile(file));
  emitter.emit('file-component:uploaded');
}

function uploadSuccess(file, response) {
  // previewElement may be null when the dropzone's previewsContainer points to a
  // non-existent DOM element, so Dropzone skips preview rendering for the file.
  if (file.previewElement) {
    file.previewElement.innerHTML = "";
  }
  if (props.uploadSuccess) {
    props.uploadSuccess(file, response);
  }
  // viz komentář v uploadMultipleSuccess - odstranit jen tento konkrétní soubor
  customDropzoneRef.value.removeFile(file);
  emitter.emit('file-component:uploaded');
}

function onRemovedFile(file) {
  if (isUnmounting) {
    return;
  }
  if (props.onRemoveFile) {
    props.onRemoveFile(file);
  }
}

// Umožní rodiči "předvyplnit" dropzone soubory, které už byly nahrané dřív
// (dropzone-vue3 postaví mock preview se stejným vzhledem jako u reálného uploadu).
function manuallyAddFile(file, fileUrl) {
  customDropzoneRef.value.manuallyAddFile(file, fileUrl);
}

function removeAllFiles(cancelIfNecessary) {
  customDropzoneRef.value.removeAllFiles(cancelIfNecessary);
}

defineExpose({manuallyAddFile, removeAllFiles});
</script>

<template>
  <div class="">
    <div :class="{'dropzone-minimal': minimal}" class="dropzone-container py-3">
      <dropzone
          id="customDropzone"
          ref="customDropzoneRef"
          :options="dropzoneOptions"
          :useCustomSlot=true
          @vdropzone-success="uploadSuccess"
          @vdropzone-success-multiple="uploadMultipleSuccess"
          @vdropzone-error="onUploadError"
          @vdropzone-error-multiple="onUploadMultipleError"
          @vdropzone-removed-file="onRemovedFile"
          @vdropzone-sending="onSend">
        <div v-if="!minimal" class="dropzone-custom-content">
          <span class="mdi mdi-cloud-upload-outline upload-icon mdi-36px"></span>
          <h3 class="dropzone-custom-title">{{ t('customDropzone.select_drag_file') }}</h3>
          <div class="subtitle">{{ t('customDropzone.maximum_file_size', {max: dropzoneOptions.maxFilesize}) }} {{ dropzoneOptions.acceptedFiles }}</div>
        </div>
        <div v-if="minimal" class="dropzone-custom-content row align-items-center justify-content-center px-3 py-1">
          <div class="col-auto px-1 px-md-2 icon-wrapper">
            <span class="mdi mdi-cloud-upload-outline upload-icon mdi-36px"></span>
          </div>
          <div class="col px-1 px-md-2 text-start">
            <h3 class="dropzone-custom-title">{{ t('customDropzone.select_drag_file') }}</h3>
            <div class="subtitle">{{ t('customDropzone.maximum_file_size', {max: dropzoneOptions.maxFilesize}) }} {{ dropzoneOptions.acceptedFiles }}</div>
          </div>
        </div>
        <div id="dz-previews"></div>
      </dropzone>
    </div>
  </div>
</template>
